import React from "react";
import { Button, Row } from "reactstrap";
import { getAnalytics, logEvent } from "firebase/analytics";
import {
  APPLE_URL,
  ANDROID_URL,
  OBJECTION_FAQS,
  TESTIMONIALS,
  USAGE_PROOF,
} from "variables/publicContent";

export function detectDeviceType() {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  if (/iPhone|iPad|iPod/i.test(ua)) return "iPhone";
  if (/android/i.test(ua)) return "Android";
  return "Web";
}

// Web visitors go to registration; phones go to the store.
export function AppCta({ deviceType, label, color = "secondary", className }) {
  const go = () => {
    if (deviceType === "iPhone") {
      logEvent(getAnalytics(), "get_app_apple");
      document.location = APPLE_URL;
    } else if (deviceType === "Android") {
      logEvent(getAnalytics(), "get_app_android");
      document.location = ANDROID_URL;
    } else {
      document.location = "/auth/register";
    }
  };
  const icon =
    deviceType === "iPhone"
      ? "fab fa-apple"
      : deviceType === "Android"
      ? "fab fa-android"
      : null;
  return (
    <Row className={"justify-content-center " + (className || "")}>
      <Button className="btn-icon btn-3" color={color} type="button" onClick={go}>
        {icon && (
          <span className="btn-inner--icon">
            <i className={icon} />
          </span>
        )}
        <span className="btn-inner--text">{label}</span>
      </Button>
    </Row>
  );
}

export function PaymentSafetyLine({ className, tone = "text-muted" }) {
  return (
    <p className={`small ${tone} text-center mt-2 mb-0 ${className || ""}`}>
      Payments processed by Stripe. Cancel anytime from your account. We don't
      sell your data -{" "}
      <a href="/auth/privacy" className={tone}>
        <u>Privacy Policy</u>
      </a>
      .
    </p>
  );
}

// Hidden until USAGE_PROOF holds a real number from the app database.
export function UsageProofStrip() {
  if (!USAGE_PROOF) return null;
  return (
    <p className="small text-center text-light mt-2 mb-0">
      {USAGE_PROOF.pros} pros sharing on {USAGE_PROOF.waters} waters this week
    </p>
  );
}

// Hidden until TESTIMONIALS holds real, permissioned quotes.
export function Testimonials({ wrap }) {
  if (!TESTIMONIALS.length) return null;
  const body = (
    <div className="d-flex flex-wrap justify-content-center">
      {TESTIMONIALS.map((t) => (
        <figure key={t.name} className="m-3 text-center" style={{ maxWidth: 260 }}>
          {t.photo && (
            <img
              src={t.photo}
              alt={`${t.name}, ${t.role}`}
              className="rounded-circle mb-2"
              style={{ width: 64, height: 64, objectFit: "cover" }}
            />
          )}
          <blockquote className="mb-1">“{t.quote}”</blockquote>
          <figcaption className="small text-muted">
            {t.name}, {t.role}
          </figcaption>
        </figure>
      ))}
    </div>
  );
  return wrap ? wrap(body) : body;
}

export function FaqList({ items = OBJECTION_FAQS, headingTag: H = "h3" }) {
  return items.map((f) => (
    <div key={f.id} className="mb-4 text-left">
      <H className="h4 mb-1">{f.q}</H>
      <p className="text-muted mb-0">{f.a}</p>
    </div>
  ));
}

// Injects FAQPage JSON-LD for the given Q&As while the page is mounted.
export function useFaqSchema(items = OBJECTION_FAQS) {
  React.useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(el);
    return () => el.remove();
  }, [items]);
}
